import type { Account, JournalEntry } from '@erp/shared';
import Decimal from 'decimal.js';
import { ArrowLeftRight, Check, Pencil, Plus, Scale, Trash2, TriangleAlert, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Card, Combobox, type ComboboxOption, FormField, Input, NumberInput, Skeleton } from '../../components/ui/index.js';
import { formatAccountBalance, loadAccountBalances, type AccountBalanceMap } from '../../lib/accountBalances.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { effectiveRateFor, formatRate, loadExchangeRates, type RateRow, saveExchangeRate } from '../../lib/exchangeRates.js';
import { formatMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

interface JeLine {
  accountId: string;
  description: string;
  debit: string;
  credit: string;
  currencyCode: string;
}

const blankLine = (currency: string): JeLine => ({
  accountId: '',
  description: '',
  debit: '0',
  credit: '0',
  currencyCode: currency,
});

/** Keep amount inputs numeric-only (digits + one dot) while staying typeable. */
const cleanAmount = (v: string): string => {
  const c = v.replace(/[^\d.]/g, '');
  const dot = c.indexOf('.');
  return dot < 0 ? c : c.slice(0, dot + 1) + c.slice(dot + 1).replace(/\./g, '');
};

export function JournalEntryFormPage(): JSX.Element {
  const { t, i18n } = useTranslation('gl');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id?: string }>();
  const isEdit = !!editId;
  const tenant = useAuthStore((s) => s.tenant);
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';

  const [accounts, setAccounts] = useState<Account[] | null>(null);
  // accountId → current base-currency balance, shown under each account picker so
  // you can see what is in an account without leaving the entry.
  const [balances, setBalances] = useState<AccountBalanceMap>(new Map());
  const [rates, setRates] = useState<RateRow[]>([]);
  const [loadingEntry, setLoadingEntry] = useState(isEdit);
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<JeLine[]>([blankLine(baseCurrency), blankLine(baseCurrency)]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Inline rate editor: which currency is being edited + the typed "1 CUR = X BASE" value.
  const [rateEdit, setRateEdit] = useState<{ currency: string; value: string } | null>(null);
  const [rateSaving, setRateSaving] = useState(false);

  const loadRates = (): void => {
    void loadExchangeRates().then(setRates);
  };

  useEffect(() => {
    void api().get<{ items: Account[] }>('/accounts').then((r) => setAccounts(r.data.items));
    // Account balances. The trial balance only lists accounts that have MOVED, so
    // anything absent is simply zero — never blank.
    void loadAccountBalances().then(setBalances).catch(() => undefined);
    loadRates();
  }, []);

  // Edit mode: prefill from the existing entry (a clean 4dp string → typeable).
  useEffect(() => {
    if (!editId) return;
    const num = (s: string): string => { const n = Number(s); return n ? String(n) : '0'; };
    void api()
      .get<JournalEntry>(`/journal-entries/${editId}`)
      .then((r) => {
        const e = r.data;
        setEntryDate(e.entryDate.slice(0, 10));
        setDescription(e.description ?? '');
        setLines(
          e.lines.map((l) => ({
            accountId: l.accountId,
            description: l.description ?? '',
            debit: num(l.debit),
            credit: num(l.credit),
            currencyCode: l.transactionCurrencyCode,
          })),
        );
      })
      .catch(() => setError(tc('errors:server')))
      .finally(() => setLoadingEntry(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  /**
   * Effective rate CUR→BASE for the entry date — mirrors the server exactly:
   * newest direct row with as_of ≤ entry date, else the newest inverse row
   * (BASE→CUR) inverted, else null (the server would reject with rate_not_found).
   */
  const effectiveRate = useMemo(
    () => (currency: string): Decimal | null => effectiveRateFor(rates, currency, baseCurrency, entryDate),
    [rates, entryDate, baseCurrency],
  );

  const totals = useMemo(() => {
    // Per-currency nominal sums (debit==credit only holds within one currency).
    const byCurrency = new Map<string, { debit: Decimal; credit: Decimal }>();
    // Base-converted sums — the check the SERVER enforces on every entry,
    // computed on stored (4dp) per-line values exactly like gl.post.
    let baseDebit = new Decimal(0);
    let baseCredit = new Decimal(0);
    let maxRate = new Decimal(1);
    let missingRate = false;
    for (const l of lines) {
      const g = byCurrency.get(l.currencyCode) ?? { debit: new Decimal(0), credit: new Decimal(0) };
      g.debit = g.debit.plus(safeDecimal(l.debit));
      g.credit = g.credit.plus(safeDecimal(l.credit));
      byCurrency.set(l.currencyCode, g);
      const rate = effectiveRate(l.currencyCode);
      if (!rate) { missingRate = true; continue; }
      if (rate.gt(maxRate)) maxRate = rate;
      baseDebit = baseDebit.plus(safeDecimal(l.debit).times(rate).toDecimalPlaces(4));
      baseCredit = baseCredit.plus(safeDecimal(l.credit).times(rate).toDecimalPlaces(4));
    }
    const groups = [...byCurrency.entries()].map(([currency, g]) => ({
      currency,
      debit: g.debit.toFixed(4),
      credit: g.credit.toFixed(4),
      balanced: g.debit.eq(g.credit),
    }));
    const multiCurrency = groups.length > 1;
    const anyAmount = groups.some((g) => new Decimal(g.debit).gt(0) || new Decimal(g.credit).gt(0));
    // Δ in base. A coarse-currency line (rate ≫ 1) can only express base values
    // on a 0.0001×rate grid, so the server absorbs residuals up to this
    // tolerance into the coarsest line (see api lib/baseRounding.ts) — mirror
    // it so the form enables submit for entries the server will accept.
    const baseDiff = baseDebit.minus(baseCredit);
    const tolerance = maxRate.times('0.00005').plus(new Decimal('0.0001').times(lines.length));
    const baseBalanced = baseDiff.abs().lte(tolerance);
    const canSubmit = anyAmount && !missingRate && (multiCurrency ? baseBalanced : groups.every((g) => g.balanced));
    return { groups, multiCurrency, anyAmount, missingRate, baseDebit, baseCredit, baseDiff, baseBalanced, canSubmit };
  }, [lines, effectiveRate]);

  // Non-base currencies present on the lines → the rates strip.
  const usedForeign = useMemo(
    () => [...new Set(lines.map((l) => l.currencyCode))].filter((c) => c !== baseCurrency),
    [lines, baseCurrency],
  );

  if (!accounts || loadingEntry) return <Card><Skeleton className="h-32 w-full" /></Card>;

  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const updateLine = (idx: number, patch: Partial<JeLine>): void => {
    // Normalize debit/credit to ASCII (Arabic / Persian digits → 0-9) so the
    // stored + POSTed value is always parseable and Decimal never throws.
    const clean: Partial<JeLine> = { ...patch };
    if (typeof clean.debit === 'string') clean.debit = normalizeNumericInput(clean.debit);
    if (typeof clean.credit === 'string') clean.credit = normalizeNumericInput(clean.credit);
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...clean } : l)));
  };
  const addLine = (): void => setLines((prev) => [...prev, blankLine(baseCurrency)]);
  const removeLine = (idx: number): void => setLines((prev) => prev.filter((_, i) => i !== idx));

  /** Fill this line's amount so the whole entry balances in the base currency. */
  const fillLine = (idx: number): void => {
    const rate = effectiveRate(lines[idx]!.currencyCode);
    if (!rate || rate.isZero()) return;
    let diff = new Decimal(0); // base debits − credits, excluding this line
    lines.forEach((l, i) => {
      if (i === idx) return;
      const r = effectiveRate(l.currencyCode);
      if (!r) return;
      diff = diff.plus(new Decimal(l.debit || '0').times(r)).minus(new Decimal(l.credit || '0').times(r));
    });
    const amount = diff.abs().dividedBy(rate).toDecimalPlaces(4).toString();
    if (diff.gt(0)) updateLine(idx, { credit: amount, debit: '0' });
    else if (diff.lt(0)) updateLine(idx, { debit: amount, credit: '0' });
  };

  /** Append a base-currency line absorbing the residual FX difference (rate = 1, always exact). */
  const addBalancingLine = (): void => {
    const diff = totals.baseDiff;
    if (diff.isZero()) return;
    setLines((prev) => [
      ...prev,
      {
        accountId: '',
        description: t('manualEntry.fxDifference', { defaultValue: 'Exchange difference' }),
        debit: diff.lt(0) ? diff.abs().toFixed(4) : '0',
        credit: diff.gt(0) ? diff.toFixed(4) : '0',
        currencyCode: baseCurrency,
      },
    ]);
  };

  const saveRate = async (): Promise<void> => {
    if (!rateEdit || !Number(rateEdit.value)) return;
    setRateSaving(true);
    try {
      // Stored as CUR→BASE ("1 USD = 1,410 IQD" when base is IQD) — the exact
      // direction the GL looks up first. Effective from the entry date.
      await saveExchangeRate(rateEdit.currency, baseCurrency, new Decimal(rateEdit.value).toFixed(8).replace(/0+$/, '').replace(/\.$/, ''), entryDate);
      setRateEdit(null);
      loadRates();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setRateSaving(false);
    }
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (lines.some((l) => !l.accountId)) {
      setError(t('manualEntry.errors.missingAccount'));
      return;
    }
    if (!totals.canSubmit) {
      setError(t('manualEntry.errors.unbalanced'));
      return;
    }
    setSaving(true);
    try {
      const body = {
        entryDate,
        description: description || undefined,
        lines: lines.map((l) => ({
          accountId: l.accountId,
          description: l.description || undefined,
          debit: l.debit || '0',
          credit: l.credit || '0',
          currencyCode: l.currencyCode,
        })),
      };
      if (isEdit) await api().patch(`/journal-entries/${editId}`, body);
      else await api().post('/journal-entries', body);
      navigate('/accounting/journals');
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string; cause?: unknown } } }).response?.data;
      const cause = typeof data?.cause === 'string' ? data.cause : '';
      if (data?.code === 'currency.rate_not_found') setError(t('manualEntry.errors.rateNotFound', { defaultValue: 'No exchange rate is set for one of the currencies on this date — add it in the rates bar above.' }));
      else if (cause === 'unbalanced_base_currency') setError(t('manualEntry.errors.unbalancedBase', { defaultValue: 'The entry does not balance in the base currency at the current rates.' }));
      else if (cause === 'unbalanced') setError(t('manualEntry.errors.unbalanced'));
      else if (cause === 'period_closed') setError(t('manualEntry.errors.periodClosed', { defaultValue: 'That date falls in a closed accounting period.' }));
      else if (cause === 'not_manual' || cause === 'is_a_reversal') setError(t('manualEntry.errors.notEditable', { defaultValue: 'Only manual entries can be edited — this one was posted by another document.' }));
      else if (data?.code === 'conflict') setError(t('journals.errors.alreadyReversed', { defaultValue: 'This entry has already been reversed.' }));
      else if (data?.code === 'unprocessable') setError(t('manualEntry.errors.serverRejected'));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (amt: string, currency: string): string =>
    formatMoney(amt, { locale: i18n.language, currency });

  /** An account's balance in every currency it holds — absent from the trial balance ⇒ zero. */
  const balanceLabel = (id: string): string =>
    `${t('manualEntry.line.balance', { defaultValue: 'Balance' })}: ${formatAccountBalance(balances.get(id), i18n.language, baseCurrency)}`;

  const accountOptions: ComboboxOption[] = accounts
    .filter((a) => a.isActive)
    .map((a) => ({
      value: a.id,
      label: `${a.code} — ${a.name}`,
      sublabel: balanceLabel(a.id),
      keywords: `${a.code} ${a.name}`,
    }));

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{isEdit ? t('manualEntry.editTitle', { defaultValue: 'Edit journal entry' }) : t('manualEntry.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{isEdit ? t('manualEntry.editDescription', { defaultValue: 'Replaces the entry in place. Only manual entries in an open period can be edited.' }) : t('manualEntry.description')}</p>
      </header>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Card className="glass-panel">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="je-date" label={t('manualEntry.date')} required>
              <Input id="je-date" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
            </FormField>
            <FormField id="je-desc" label={t('manualEntry.entryDescription')}>
              <Input id="je-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('manualEntry.descriptionPlaceholder', { defaultValue: 'e.g. Transfer USD cash into the IQD drawer' })} />
            </FormField>
          </div>

          {/* Exchange-rate strip — one chip per foreign currency in use. */}
          {usedForeign.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />
                {t('manualEntry.ratesOn', { defaultValue: 'Rates on {{date}}', date: entryDate })}
              </span>
              {usedForeign.map((cur) => {
                const rate = effectiveRate(cur);
                const editing = rateEdit?.currency === cur;
                if (editing) {
                  return (
                    <span key={cur} className="inline-flex items-center gap-1.5 rounded-full border border-brand/40 bg-brand/5 py-1 pe-1 ps-3 text-sm" dir="ltr">
                      <span className="font-mono text-xs">1 {cur} =</span>
                      <NumberInput
                        autoFocus
                        value={rateEdit.value}
                        onValueChange={(raw) => setRateEdit({ currency: cur, value: cleanAmount(raw) })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void saveRate(); } }}
                        className="h-7 w-28 text-end font-mono text-sm"
                        aria-label={t('manualEntry.rateValue', { defaultValue: 'Rate' })}
                      />
                      <span className="font-mono text-xs">{baseCurrency}</span>
                      <Button type="button" size="sm" onClick={() => void saveRate()} loading={rateSaving} aria-label={tc('actions.save', { defaultValue: 'Save' })}>
                        <Check className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </span>
                  );
                }
                return rate ? (
                  <button
                    key={cur}
                    type="button"
                    onClick={() => setRateEdit({ currency: cur, value: rate.toSignificantDigits(8).toString() })}
                    className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-bg px-3 py-1 font-mono text-sm transition hover:border-brand/40"
                    dir="ltr"
                    title={t('manualEntry.editRate', { defaultValue: 'Update this rate' })}
                  >
                    1 {cur} = {formatRate(rate, i18n.language)} {baseCurrency}
                    <Pencil className="h-3 w-3 text-fg-subtle transition group-hover:text-brand" aria-hidden />
                  </button>
                ) : (
                  <button
                    key={cur}
                    type="button"
                    onClick={() => setRateEdit({ currency: cur, value: '' })}
                    className="inline-flex items-center gap-1.5 rounded-full border border-warning/50 bg-warning/10 px-3 py-1 text-sm font-medium text-warning transition hover:border-warning"
                  >
                    <TriangleAlert className="h-3.5 w-3.5" aria-hidden />
                    {t('manualEntry.rateMissing', { defaultValue: 'No {{cur}} rate — set it', cur })}
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="overflow-x-auto p-0">
          <table className="w-full text-start text-sm">
            <thead className="bg-bg-muted text-xs uppercase tracking-wide text-fg-subtle">
              <tr>
                <th className="px-3 py-2.5 text-start">{t('manualEntry.line.account')}</th>
                <th className="px-3 py-2.5 text-start">{t('manualEntry.line.description')}</th>
                <th className="w-24 px-3 py-2.5 text-start">{t('manualEntry.line.currency')}</th>
                <th className="w-36 px-3 py-2.5 text-end">{t('manualEntry.line.debit')}</th>
                <th className="w-36 px-3 py-2.5 text-end">{t('manualEntry.line.credit')}</th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => {
                const rate = line.currencyCode !== baseCurrency ? effectiveRate(line.currencyCode) : null;
                const amt = new Decimal(line.debit || '0').gt(0) ? line.debit : line.credit;
                const baseEquiv = rate && Number(amt) > 0 ? new Decimal(amt).times(rate) : null;
                return (
                  <tr key={idx} className="border-t border-border align-top transition hover:bg-bg-subtle/40">
                    <td className="px-3 py-2">
                      <Combobox
                        options={accountOptions}
                        value={line.accountId || undefined}
                        onChange={(v) => {
                          // Picking an account flips the line to the ACCOUNT's currency —
                          // an IQD cash account naturally takes IQD amounts.
                          const acc = v ? accountById.get(v) : undefined;
                          updateLine(idx, { accountId: v ?? '', ...(acc?.currencyCode ? { currencyCode: acc.currencyCode } : {}) });
                        }}
                        placeholder={t('manualEntry.line.account', { defaultValue: 'Account' })}
                        ariaLabel={t('manualEntry.line.account', { defaultValue: 'Account' })}
                        size="sm"
                      />
                      {/* The chosen account's current balance, so you can see what
                          is in it without leaving the entry. */}
                      {line.accountId && (
                        <p className="mt-1 text-xs font-semibold tabular-nums text-brand">{balanceLabel(line.accountId)}</p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Input value={line.description} onChange={(e) => updateLine(idx, { description: e.target.value })} />
                    </td>
                    <td className="px-3 py-2">
                      <CurrencySelect value={line.currencyCode} onChange={(c) => updateLine(idx, { currencyCode: c })} />
                    </td>
                    <td className="px-3 py-2">
                      <NumberInput
                        className="text-end font-mono"
                        value={line.debit}
                        onValueChange={(raw) => updateLine(idx, { debit: cleanAmount(raw), credit: '0' })}
                        aria-label={t('manualEntry.line.debit')}
                      />
                      {baseEquiv && new Decimal(line.debit || '0').gt(0) && (
                        <div className="mt-0.5 text-end font-mono text-xs text-fg-subtle" dir="ltr">≈ {fmt(baseEquiv.toFixed(4), baseCurrency)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <NumberInput
                        className="text-end font-mono"
                        value={line.credit}
                        onValueChange={(raw) => updateLine(idx, { credit: cleanAmount(raw), debit: '0' })}
                        aria-label={t('manualEntry.line.credit')}
                      />
                      {baseEquiv && new Decimal(line.credit || '0').gt(0) && (
                        <div className="mt-0.5 text-end font-mono text-xs text-fg-subtle" dir="ltr">≈ {fmt(baseEquiv.toFixed(4), baseCurrency)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-0.5">
                        {totals.multiCurrency && !totals.baseBalanced && Number(line.debit || '0') === 0 && Number(line.credit || '0') === 0 && (
                          <button
                            type="button"
                            onClick={() => fillLine(idx)}
                            className="grid h-8 w-8 place-items-center rounded-md text-fg-muted transition hover:bg-brand/10 hover:text-brand"
                            title={t('manualEntry.line.fill', { defaultValue: 'Fill the amount that balances the entry' })}
                          >
                            <Wand2 className="h-4 w-4" aria-hidden />
                          </button>
                        )}
                        {lines.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeLine(idx)}
                            className="grid h-8 w-8 place-items-center rounded-md text-fg-muted transition hover:bg-danger/10 hover:text-danger"
                            aria-label={t('manualEntry.line.remove', { defaultValue: 'Remove line' })}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {totals.groups.map((g) => (
                <tr key={g.currency} className="border-t border-border bg-bg-muted/60">
                  <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted" colSpan={2}>{t('manualEntry.totals')}</td>
                  <td className="px-3 py-2 font-mono text-xs text-fg-muted">{g.currency}</td>
                  <td className="px-3 py-2 text-end font-mono">{fmt(g.debit, g.currency)}</td>
                  <td className="px-3 py-2 text-end font-mono">{fmt(g.credit, g.currency)}</td>
                  <td className="px-3 py-2 text-center">
                    {g.balanced ? (
                      <span className="text-success" title={t('manualEntry.groupBalanced', { defaultValue: 'Debits equal credits' })}>✓</span>
                    ) : (
                      <span className="text-warning" title={t('manualEntry.groupUnbalanced', { defaultValue: 'Debits and credits differ in this currency' })}>≠</span>
                    )}
                  </td>
                </tr>
              ))}
              {totals.multiCurrency && !totals.missingRate && (
                <tr className={`border-t-2 ${totals.baseBalanced ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5'}`}>
                  <td className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide" colSpan={2}>
                    <span className="inline-flex items-center gap-1.5">
                      <Scale className="h-3.5 w-3.5" aria-hidden />
                      {t('manualEntry.baseTotals', { defaultValue: 'Converted to {{cur}}', cur: baseCurrency })}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-fg-muted">{baseCurrency}</td>
                  <td className="px-3 py-2.5 text-end font-mono font-semibold">{fmt(totals.baseDebit.toFixed(4), baseCurrency)}</td>
                  <td className="px-3 py-2.5 text-end font-mono font-semibold">{fmt(totals.baseCredit.toFixed(4), baseCurrency)}</td>
                  <td className="px-3 py-2.5 text-center">
                    {totals.baseBalanced ? (
                      <span className="font-bold text-success">✓</span>
                    ) : (
                      <span className="font-mono text-xs font-bold text-warning" dir="ltr">Δ {fmt(totals.baseDiff.abs().toFixed(4), baseCurrency)}</span>
                    )}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
          <div className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2">
            <Button type="button" variant="secondary" size="sm" onClick={addLine}>
              <Plus className="me-1 h-3.5 w-3.5" aria-hidden />
              {t('manualEntry.line.add')}
            </Button>
            {totals.multiCurrency && !totals.baseBalanced && !totals.missingRate && totals.anyAmount && (
              <Button type="button" variant="secondary" size="sm" onClick={addBalancingLine}>
                <Scale className="me-1 h-3.5 w-3.5" aria-hidden />
                {t('manualEntry.addBalancing', { defaultValue: 'Add balancing line ({{amount}})', amount: fmt(totals.baseDiff.abs().toFixed(4), baseCurrency) })}
              </Button>
            )}
          </div>
        </Card>

        {totals.missingRate ? (
          <p className="text-sm text-warning">
            {t('manualEntry.rateMissingHint', { defaultValue: 'An exchange rate is missing for this date — set it in the rates bar above before posting.' })}
          </p>
        ) : totals.multiCurrency && !totals.baseBalanced && totals.anyAmount ? (
          <p className="text-sm text-fg-muted">
            {t('manualEntry.crossCurrencyDiffHint', { defaultValue: 'The entry is off by {{amount}} after conversion — adjust an amount (magic wand fills it for you) or add a balancing line to an exchange gain/loss account.', amount: fmt(totals.baseDiff.abs().toFixed(4), baseCurrency) })}
          </p>
        ) : !totals.canSubmit && totals.anyAmount ? (
          <p className="text-sm text-warning">
            {t('manualEntry.errors.unbalancedHint', { defaultValue: 'Sum of debits must equal sum of credits before posting.' })}
          </p>
        ) : null}
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" loading={saving} disabled={!totals.canSubmit}>
            {isEdit ? t('manualEntry.saveChanges', { defaultValue: 'Save changes' }) : t('manualEntry.post')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/accounting/journals')} disabled={saving}>
            {tc('actions.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
